import axios from "axios";
import { persistAccessToken } from "../../utils/jwt";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_PATH,
});

const token = localStorage.getItem("accessToken");

if (token) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
}

// Single-flight token refresh: many requests can 401 at once after the backend
// rotates a token, but only one refresh call should run — the rest await it.
let refreshPromise = null;

const refreshAccessToken = async () => {
  const currentToken = localStorage.getItem("accessToken");
  if (!currentToken) throw new Error("No token to refresh");

  const resp = await axios.post(
    `${process.env.REACT_APP_API_PATH}auth/refresh`,
    {},
    {
      headers: {
        Authorization: `Bearer ${currentToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  );

  const newToken = resp.data?.data?.token;
  if (!newToken) throw new Error("Refresh response missing token");
  persistAccessToken(newToken); // updates accessToken + tokenExpiry (and notifies other tabs)
  return newToken;
};

// Shared with workConfig.js so both API clients wait on the same refresh.
export const refreshAccessTokenOnce = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const forceLogout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("loginTimestamp");
  window.location.href = "/";
};

apiClient.interceptors.request.use(
  (config) => {
    config.params = {
      // api_token: localStorage.getItem("accessToken"),
      ...config.params,
    };
    
    // Add organization ID header if available
    const selectedOrganizationId = localStorage.getItem('selectedOrganizationId') || localStorage.getItem('organization-id');
    if (selectedOrganizationId) {
      config.headers = {
        ...config.headers,
        'organization-id': selectedOrganizationId,
      };
    }
    
    return {
      ...config,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("accessToken"),
        ...config.headers,
      },
    };
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || "";
    const isAuthCall = url.includes("auth/refresh") || url.includes("auth/logout");

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthCall) {
      // A 401 here is usually a token that was just rotated (by this tab, another
      // tab, or a near-expiry refresh). Try one refresh + retry before giving up,
      // so a lost rotation race doesn't kill the whole session.
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessTokenOnce();
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(originalRequest);
      } catch {
        // Refresh genuinely failed — the session is dead; log out.
        forceLogout();
        return Promise.reject(error.response?.data ?? error);
      }
    }

    // 401 on an auth call, or after a failed retry: hard logout.
    if (status === 401) {
      forceLogout();
    }

    return Promise.reject(error.response?.data ?? error);
  }
);

const { get, post, put, delete: destroy, patch } = apiClient;
export { get, post, put, destroy, patch };